import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
//import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SvgIcon from "@mui/material/SvgIcon";
import { ReactComponent as IconTeacher } from "../../imagenes/Iconos/Iconos_botones/iconoprofesor.svg"
import { createTeacherDelay } from "../../api/ticketingApi";

const TEAL = "#21a598";
const TEAL_DARK = "#1b887f";

type Props = {
  reservaId: number;
  buttonText?: string;
  defaultMinutes?: number;
  onSuccess?: (ticketId: string) => void;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
};

function TeacherIcon() {
  return (
    <SvgIcon
      component={IconTeacher}
      inheritViewBox
      sx={{
        width: 25,
        height: 25,
        verticalAlign: "middle",
      }}
    />
  );
}

export default function TeacherDelayButton({
  reservaId,
  buttonText = "",
  defaultMinutes = 10,
  onSuccess,
  variant = "outlined",
  size = "small",
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [minutesLate, setMinutesLate] = React.useState<number>(defaultMinutes);
  const [comment, setComment] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    sev: "success" | "error";
  }>({ open: false, msg: "", sev: "success" });

  const onChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const submit = async () => {
    if (!minutesLate || minutesLate < 1) {
      setSnack({ open: true, msg: "Los minutos deben ser ≥ 1", sev: "error" });
      return;
    }
    setLoading(true);
    try {
      const res = await createTeacherDelay(reservaId, minutesLate, comment, files);
      const ticketId = res.id || res.ticketId || "";
      setSnack({
        open: true,
        msg: res.dedup ? `Adjuntado al ticket ${ticketId}` : `Ticket creado: ${ticketId}`,
        sev: "success",
      });
      onSuccess?.(ticketId);
      setOpen(false);
      setComment("");
      setFiles([]);
      setMinutesLate(defaultMinutes);
    } catch (e: any) {
      setSnack({
        open: true,
        msg: e?.response?.data?.message || "Error creando el ticket",
        sev: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<TeacherIcon />}
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        title="Retraso del/a profesor/a"
        sx={{

          minWidth: "auto",
          px: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: TEAL,
          borderColor: TEAL,
          "&:hover": {
            backgroundColor: "rgba(33,165,152,0.08)",
            borderColor: TEAL_DARK,
            color: TEAL_DARK,
          },
          "& .MuiButton-startIcon": { mr: 0 },
          "& .MuiSvgIcon-root": { color: "inherit" },
        }}
      >
        {buttonText}
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reportar retraso del/a profesor/a</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Minutos de retraso"
              type="number"
              value={minutesLate}
              onChange={(e) => setMinutesLate(Number(e.target.value))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Comentario (opcional)"
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
            />
            <Button component="label" variant="text">
              Adjuntar archivos
              <input hidden multiple type="file" onChange={onChooseFiles} />
            </Button>
            {files.length > 0 && <small>{files.length} archivo(s) seleccionados</small>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={submit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Crear ticket"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
